/**
 * Issue #24 — Web 投诉工作台 API adapter。
 *
 * 投诉 DTO、查询参数与响应全部从 OpenAPI 生成类型派生。前端只负责传递当前筛选和
 * version；orgId、employeeId、类别授权、审计与状态机均由后端重新校验。
 */
import type { components, operations } from './generated/types';
import { apiClient } from './api';

export type ComplaintCategory =
  components['schemas']['ComplaintListItem']['category'];
export type ComplaintStatus = components['schemas']['ComplaintListItem']['status'];
export type ComplaintConclusion = NonNullable<
  components['schemas']['ComplaintListItem']['conclusion']
>;
export type ComplaintMeta = components['schemas']['ComplaintMeta'];
export type ComplaintCapabilities = components['schemas']['ComplaintCapabilities'];
export type ComplaintListItem = components['schemas']['ComplaintListItem'];
export type ComplaintListResponse = components['schemas']['ComplaintListResponse'];
export type ComplaintDetail = components['schemas']['ComplaintDetail'];
export type ComplaintStartRequest = components['schemas']['ComplaintStartRequest'];
export type ComplaintRequestMoreRequest =
  components['schemas']['ComplaintRequestMoreRequest'];
export type ComplaintResolveRequest = components['schemas']['ComplaintResolveRequest'];
export type ComplaintAttachmentUrlResponse =
  components['schemas']['ComplaintAttachmentUrlResponse'];
export type ComplaintHandlersConfigView =
  components['schemas']['ComplaintHandlersConfigView'];
export type UpdateComplaintHandlersRequest =
  components['schemas']['UpdateComplaintHandlersRequest'];
export type ComplaintChannelsView = components['schemas']['ComplaintChannelsView'];
export type UpdateComplaintChannelsRequest =
  components['schemas']['UpdateComplaintChannelsRequest'];
export type ComplaintErrorCode = components['schemas']['ComplaintError']['code'];

export type ComplaintWorkbenchQuery = NonNullable<
  operations['ComplaintsAdminController_list']['parameters']['query']
>;
export type ComplaintExportQuery = NonNullable<
  operations['ComplaintsAdminController_export']['parameters']['query']
>;

function queryRecord(
  query: ComplaintWorkbenchQuery | ComplaintExportQuery,
): Record<string, unknown> {
  return query as Record<string, unknown>;
}

export function getComplaintMetaApi(): Promise<ComplaintMeta> {
  return apiClient.get<ComplaintMeta>('/complaints/meta');
}

export function listComplaintWorkbenchApi(
  query: ComplaintWorkbenchQuery,
): Promise<ComplaintListResponse> {
  return apiClient.get<ComplaintListResponse>(
    '/admin/complaints',
    queryRecord(query),
  );
}

export function getComplaintWorkbenchDetailApi(id: string): Promise<ComplaintDetail> {
  return apiClient.get<ComplaintDetail>(
    `/admin/complaints/${encodeURIComponent(id)}`,
  );
}

export function startComplaintApi(
  id: string,
  input: ComplaintStartRequest,
): Promise<ComplaintDetail> {
  return apiClient.post<ComplaintDetail>(
    `/admin/complaints/${encodeURIComponent(id)}/start`,
    input,
  );
}

export function requestMoreComplaintApi(
  id: string,
  input: ComplaintRequestMoreRequest,
): Promise<ComplaintDetail> {
  return apiClient.post<ComplaintDetail>(
    `/admin/complaints/${encodeURIComponent(id)}/need-more`,
    input,
  );
}

export function resolveComplaintApi(
  id: string,
  input: ComplaintResolveRequest,
): Promise<ComplaintDetail> {
  return apiClient.post<ComplaintDetail>(
    `/admin/complaints/${encodeURIComponent(id)}/resolve`,
    input,
  );
}

export function reviewComplaintAppealApi(
  id: string,
  input: ComplaintResolveRequest,
): Promise<ComplaintDetail> {
  return apiClient.post<ComplaintDetail>(
    `/admin/complaints/${encodeURIComponent(id)}/appeal-review`,
    input,
  );
}

export function getComplaintWorkbenchAttachmentUrlApi(
  complaintId: string,
  attachmentId: string,
): Promise<ComplaintAttachmentUrlResponse> {
  return apiClient.get<ComplaintAttachmentUrlResponse>(
    `/admin/complaints/${encodeURIComponent(complaintId)}/attachments/${encodeURIComponent(attachmentId)}/url`,
  );
}

/** 当前筛选全量导出；分页字段在调用边界被显式剔除，CSV 只能来自真实后端。 */
export function exportComplaintWorkbenchApi(query: ComplaintWorkbenchQuery) {
  const { page: _page, pageSize: _pageSize, ...filters } = query;
  return apiClient.download('/admin/complaints/export', {
    query: queryRecord(filters),
    expectContentType: 'text/csv',
    fallbackFileName: 'complaints.csv',
  });
}

export function getComplaintHandlersApi(): Promise<ComplaintHandlersConfigView> {
  return apiClient.get<ComplaintHandlersConfigView>(
    '/admin/complaints/config/handlers',
  );
}

export function updateComplaintHandlersApi(
  input: UpdateComplaintHandlersRequest,
): Promise<ComplaintHandlersConfigView> {
  return apiClient.put<ComplaintHandlersConfigView>(
    '/admin/complaints/config/handlers',
    input,
  );
}

export function getComplaintChannelsApi(): Promise<ComplaintChannelsView> {
  return apiClient.get<ComplaintChannelsView>(
    '/admin/complaints/config/channels',
  );
}

export function updateComplaintChannelsApi(
  input: UpdateComplaintChannelsRequest,
): Promise<ComplaintChannelsView> {
  return apiClient.put<ComplaintChannelsView>(
    '/admin/complaints/config/channels',
    input,
  );
}
